import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import SinapiService from '../services/SinapiService';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const IMPORT_STEPS = [
  { key: 'ISD', label: 'Insumos Nao Desonerados', color: 'blue' },
  { key: 'ICD', label: 'Insumos Desonerados', color: 'green' },
  { key: 'ISE', label: 'Insumos Sem Encargos', color: 'purple' },
  { key: 'CSD', label: 'Composicoes Nao Desoneradas', color: 'blue' },
  { key: 'CCD', label: 'Composicoes Desoneradas', color: 'green' },
  { key: 'CSE', label: 'Composicoes Sem Encargos', color: 'purple' },
  { key: 'ANALITICO', label: 'Analitico', color: 'orange' },
];

function initStepStates() {
  const s = {};
  IMPORT_STEPS.forEach(({ key }) => {
    s[key] = { status: 'pending', count: null, error: null };
  });
  return s;
}

function AdminSinapi() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dbStatus, setDbStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Upload state
  const [mesRef, setMesRef] = useState(new Date().toISOString().slice(0, 7));
  const [file, setFile] = useState(null);
  const [filePath, setFilePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Step states
  const [steps, setSteps] = useState(initStepStates);
  const [activeStep, setActiveStep] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await SinapiService.getStatus();
      setDbStatus(data);
    } catch (err) {
      console.error('Erro ao carregar status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);

    try {
      const uploadData = await SinapiService.getUploadUrl(user.email);
      await SinapiService.uploadToStorage(uploadData.signedUrl, file);
      setFilePath(uploadData.path);
      setSteps(initStepStates());
      LogService.log(LOG_ACTIONS.SINAPI_IMPORT_START, { details: { mesRef, fileName: file.name } });
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleStep = async (stepKey) => {
    if (!filePath) return;
    setActiveStep(stepKey);
    setSteps((prev) => ({ ...prev, [stepKey]: { status: 'running', count: null, error: null } }));

    try {
      const result = await SinapiService.importStep({
        mesRef,
        adminEmail: user.email,
        filePath,
        step: stepKey,
      });
      if (result.debug) {
        setDebugInfo((prev) => ({ ...prev, [stepKey]: result.debug }));
      }
      setSteps((prev) => ({ ...prev, [stepKey]: { status: 'done', count: result.count, error: null, message: result.message } }));
      loadStatus();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.error || err.message;
      if (errData?.debug) {
        setDebugInfo((prev) => ({ ...prev, [stepKey]: errData.debug }));
      }
      setSteps((prev) => ({ ...prev, [stepKey]: { status: 'error', count: null, error: msg } }));
    } finally {
      setActiveStep(null);
    }
  };

  const handleImportAll = async () => {
    for (const { key } of IMPORT_STEPS) {
      if (steps[key].status === 'done') continue;
      await handleStep(key);
      // If error, stop
      // Re-read state via closure won't work, check via ref trick
    }
  };

  const handleFinalize = async () => {
    try {
      await SinapiService.importStep({ mesRef, adminEmail: user.email, filePath, step: 'FINALIZAR' });
      loadStatus();
    } catch (err) {
      console.error('Erro ao finalizar:', err);
    }
  };

  const allDone = IMPORT_STEPS.every(({ key }) => steps[key].status === 'done');
  const anyRunning = activeStep !== null;

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('pt-BR') + ' ' + new Date(iso).toLocaleTimeString('pt-BR');
  };

  const statusIcon = (s) => {
    if (s.status === 'pending') return <span className="text-gray-300 text-lg">&#9744;</span>;
    if (s.status === 'running') return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orca-600" />;
    if (s.status === 'done') return <span className="text-green-600 text-lg">&#9745;</span>;
    if (s.status === 'error') return <span className="text-red-600 text-lg">&#9746;</span>;
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">SINAPI - Administração</h1>
            <p className="text-gray-500">Importação por etapas do arquivo SINAPI</p>
          </div>
          <div className="flex gap-2 mt-3 sm:mt-0 flex-wrap">
            <button onClick={() => navigate('/admin/dashboard')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Dashboard</button>
            <button onClick={() => navigate('/admin/logs')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Logs</button>
            <button onClick={() => navigate('/admin/feedbacks')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Feedbacks</button>
            <button onClick={() => navigate('/admin/usuarios')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Usuários</button>
            <button onClick={() => navigate('/admin/cub')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">CUB</button>
            <button onClick={loadStatus} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Atualizar</button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Composições</p>
            <p className="text-2xl font-bold text-gray-900">
              {loading ? '...' : (dbStatus?.totalComposicoes || 0).toLocaleString('pt-BR')}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Insumos</p>
            <p className="text-2xl font-bold text-gray-900">
              {loading ? '...' : (dbStatus?.totalInsumos || 0).toLocaleString('pt-BR')}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Estados com Dados</p>
            <p className="text-2xl font-bold text-gray-900">
              {loading ? '...' : (dbStatus?.estados?.length || 0)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Última Atualização</p>
            <p className="text-sm font-bold text-gray-900">
              {loading ? '...' : formatDate(dbStatus?.ultimaAtualizacao)}
            </p>
          </div>
        </div>

        {/* Step 1: Upload */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">1. Upload do Arquivo</h2>
          <p className="text-xs text-gray-400 mb-4">Envie o arquivo SINAPI_Referência (.xlsx) para o servidor</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Mês de Referência</label>
              <input
                type="month"
                value={mesRef}
                onChange={(e) => { setMesRef(e.target.value); setFilePath(null); setSteps(initStepStates()); }}
                disabled={uploading || anyRunning}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Arquivo SINAPI</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setFile(e.target.files[0] || null); setFilePath(null); setSteps(initStepStates()); setUploadError(null); }}
                disabled={uploading || anyRunning}
                className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orca-50 file:text-orca-700 hover:file:bg-orca-100 file:cursor-pointer disabled:opacity-50"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleUpload}
              disabled={!file || uploading || anyRunning}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-orca-600 rounded-lg hover:bg-orca-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Enviando ({file ? (file.size / 1024 / 1024).toFixed(1) : 0} MB)...
                </span>
              ) : 'Enviar Arquivo'}
            </button>

            {filePath && (
              <span className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                <span className="text-green-600">&#9745;</span>
                Arquivo enviado com sucesso
              </span>
            )}

            {uploadError && (
              <span className="text-sm text-red-600">{uploadError}</span>
            )}
          </div>
        </div>

        {/* Step 2: Import steps */}
        <div className={`bg-white rounded-xl border border-gray-200 p-6 mb-6 ${!filePath ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-gray-900">2. Importar Dados</h2>
            {filePath && !anyRunning && !allDone && (
              <button
                onClick={handleImportAll}
                className="px-4 py-2 text-sm font-semibold text-white bg-orca-600 rounded-lg hover:bg-orca-700 cursor-pointer transition-colors"
              >
                Importar Tudo
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mb-4">Execute cada etapa individualmente ou clique "Importar Tudo"</p>

          <div className="space-y-2">
            {IMPORT_STEPS.map(({ key, label }) => {
              const s = steps[key];
              return (
                <div key={key} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-100 bg-gray-50">
                  <div className="w-6 flex-shrink-0 flex items-center justify-center">
                    {statusIcon(s)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    {s.status === 'done' && s.count > 0 && (
                      <p className="text-xs text-green-600">{s.count.toLocaleString('pt-BR')} registros importados</p>
                    )}
                    {s.status === 'done' && s.count === 0 && (
                      <p className="text-xs text-yellow-600">{s.message || 'Aba nao encontrada ou sem dados'}</p>
                    )}
                    {s.status === 'error' && (
                      <p className="text-xs text-red-600">{s.error}</p>
                    )}
                    {s.status === 'running' && (
                      <p className="text-xs text-orca-600">Processando... nao feche a pagina</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleStep(key)}
                    disabled={!filePath || anyRunning}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      s.status === 'done'
                        ? 'text-gray-500 bg-gray-200 hover:bg-gray-300'
                        : s.status === 'error'
                          ? 'text-white bg-red-500 hover:bg-red-600'
                          : 'text-white bg-orca-600 hover:bg-orca-700'
                    }`}
                  >
                    {s.status === 'done' ? 'Reimportar' : s.status === 'error' ? 'Tentar Novamente' : 'Importar'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Finalize */}
          {allDone && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleFinalize}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors"
                >
                  Finalizar Importação
                </button>
                <span className="text-sm text-green-700">Todas as etapas concluídas!</span>
              </div>
            </div>
          )}
        </div>

        {/* Debug Panel */}
        {debugInfo && Object.keys(debugInfo).length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-bold text-green-400 mb-3">Debug Info (Parser)</h2>
            {Object.entries(debugInfo).map(([stepKey, info]) => (
              <div key={stepKey} className="mb-4 last:mb-0">
                <h3 className="text-sm font-bold text-yellow-400 mb-1">{stepKey} - Aba: {info.sheetName || 'N/A'}</h3>
                <pre className="text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(info, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}

        {/* Versions table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Histórico de Importações</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Referência</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Composições</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Insumos</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Analítico</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orca-600 mx-auto" />
                    </td>
                  </tr>
                ) : !dbStatus?.versoes?.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                      Nenhuma importação realizada
                    </td>
                  </tr>
                ) : (
                  dbStatus.versoes.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800">{v.referencia}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          v.status === 'completo' ? 'bg-green-100 text-green-700' :
                          v.status === 'erro' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{(v.qtd_composicoes || 0).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-3 text-gray-700">{(v.qtd_insumos || 0).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-3 text-gray-700">{(v.qtd_analitico || 0).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(v.data_importacao)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminSinapi;
